// src/utils/padVolumetrics.js
//
// Volumetric calculations for pad reclamation.
//
// MVP approach: uniform depth × polygon footprint (prism volume).
// The API here is intentionally shaped so a DEM-based cut/fill implementation
// can drop in later without panel/store changes — see `computeUniformVolumes`
// signature; a future `computeDemVolumes(geom, demSource)` would return the
// same shape.

import area from "@turf/area";

/* ---------------- Unit conversions ---------------- */

// Standard reclamation depths, inches → meters
export const DEPTHS_IN = [8, 12, 16];
const IN_TO_M = 0.0254;

// Area
const M2_TO_FT2    = 10.7639104;
const M2_TO_ACRES  = 0.000247105;
const M2_TO_HA     = 0.0001;

// Volume
const M3_TO_YD3    = 1.30795062;
const M3_TO_FT3    = 35.3146667;

/* ---------------- Core math ---------------- */

/**
 * Compute uniform-depth reclamation volumes for a pad polygon.
 *
 * @param {GeoJSON.Feature|GeoJSON.Geometry} geom  — polygon or multipolygon
 * @param {number[]} [depthsIn=DEPTHS_IN] — depths in inches
 * @returns {{
 *   area: { m2:number, ft2:number, acres:number, hectares:number },
 *   volumes: Array<{
 *     depthIn:number, depthM:number,
 *     m3:number, yd3:number, ft3:number
 *   }>
 * } | null}
 */
export function computeUniformVolumes(geom, depthsIn = DEPTHS_IN) {
  if (!geom) return null;

  // turf/area accepts Feature or Geometry; normalize to Feature for safety
  const feature =
    geom.type === "Feature"
      ? geom
      : { type: "Feature", geometry: geom, properties: {} };

  const m2 = area(feature);
  if (!Number.isFinite(m2) || m2 <= 0) return null;

  const volumes = depthsIn.map((depthIn) => {
    const depthM = depthIn * IN_TO_M;
    const m3 = m2 * depthM;
    return {
      depthIn,
      depthM,
      m3,
      yd3: m3 * M3_TO_YD3,
      ft3: m3 * M3_TO_FT3,
    };
  });

  return {
    area: {
      m2,
      ft2:      m2 * M2_TO_FT2,
      acres:    m2 * M2_TO_ACRES,
      hectares: m2 * M2_TO_HA,
    },
    volumes,
  };
}

/* ---------------- Road volumetrics ---------------- */

const FT_TO_M = 0.3048;

/**
 * Compute reclamation volumes for access roads given total length and an
 * assumed graded-surface width. Same depth stops as pads (8/12/16").
 *
 * @param {number} totalLengthM — sum of road lengths in meters
 * @param {number} widthFt — assumed road width in feet (default 18)
 * @param {number[]} [depthsIn=DEPTHS_IN]
 * @returns {{
 *   length: { m:number, ft:number },
 *   width:  { m:number, ft:number },
 *   area:   { m2:number, ft2:number, acres:number, hectares:number },
 *   volumes: Array<{ depthIn, depthM, m3, yd3, ft3 }>
 * } | null}
 */
export function computeRoadVolumes(totalLengthM, widthFt, depthsIn = DEPTHS_IN) {
  if (!Number.isFinite(totalLengthM) || totalLengthM <= 0) return null;
  if (!Number.isFinite(widthFt) || widthFt <= 0) return null;

  const widthM = widthFt * FT_TO_M;
  const m2 = totalLengthM * widthM;

  const volumes = depthsIn.map((depthIn) => {
    const depthM = depthIn * IN_TO_M;
    const m3 = m2 * depthM;
    return {
      depthIn,
      depthM,
      m3,
      yd3: m3 * M3_TO_YD3,
      ft3: m3 * M3_TO_FT3,
    };
  });

  return {
    length: { m: totalLengthM, ft: totalLengthM / FT_TO_M },
    width:  { m: widthM,       ft: widthFt },
    area: {
      m2,
      ft2:      m2 * M2_TO_FT2,
      acres:    m2 * M2_TO_ACRES,
      hectares: m2 * M2_TO_HA,
    },
    volumes,
  };
}

/* ---------------- Formatters ---------------- */

export function fmtArea(a) {
  if (!a) return { imperial: "—", metric: "—" };
  return {
    imperial: `${a.acres.toFixed(2)} ac`,
    metric:   `${a.hectares.toFixed(3)} ha`,
  };
}

export function fmtLength(m) {
  if (!Number.isFinite(m) || m <= 0) return { imperial: "—", metric: "—" };
  const ft = m / FT_TO_M;
  return {
    imperial: ft >= 5280
      ? `${(ft / 5280).toFixed(2)} mi`
      : `${Math.round(ft).toLocaleString()} ft`,
    metric: m >= 1000
      ? `${(m / 1000).toFixed(2)} km`
      : `${Math.round(m).toLocaleString()} m`,
  };
}

export function fmtVolume(v) {
  if (!v) return { imperial: "—", metric: "—" };
  return {
    imperial: `${Math.round(v.yd3).toLocaleString()} yd³`,
    metric:   `${Math.round(v.m3).toLocaleString()} m³`,
  };
}